/**
 * The NOS Deal picked on the New Resource Plan form, kept current
 * (src/api/plan-deal-solution.ts).
 *
 * `useRefreshPlanDeal` runs on every pick: the server re-reads the Deal and brings its
 * Solution into Staff. `useCreatePlanDealSolution` gives a Deal with no Solution one in
 * NOS from a name alone.
 *
 * Both reload the Solution picker list BEFORE resolving and hand back its fresh options.
 * The form sets the CRM solution from those options straight away, so it never looks a
 * just-imported Solution up in a list fetched before the import. Fetching the list is a
 * refetch, not an optimistic write: the server's answer is what lands in the cache.
 *
 * The refresh reports inline on the form (it runs on every pick, so a toast each time
 * would be noise); Create toasts like every other mutation.
 */

import { type QueryClient, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import type { PlanDealOption } from "@/api/plan-deal-options";
import { fetchDashboardSolutions, toSolutionPickerOptions } from "@/hooks/use-dashboard-solutions";
import { jsonFetch } from "@/lib/json-fetch";
import type { SelectOption } from "@/lib/option-search";
import { planDealOptionKeys, solutionDashboardKeys } from "@/lib/query";

export interface RefreshedPlanDeal {
  deal: PlanDealOption;
  /** The Solution picker options, reloaded after the server's import. */
  solutionOptions: SelectOption[];
}

async function withFreshSolutions(
  queryClient: QueryClient,
  deal: PlanDealOption,
): Promise<RefreshedPlanDeal> {
  const data = await queryClient.fetchQuery({
    queryKey: solutionDashboardKeys.all,
    queryFn: fetchDashboardSolutions,
    staleTime: 0,
  });
  return { deal, solutionOptions: toSolutionPickerOptions(data) };
}

const dealUrl = (dealId: string, action: "refresh" | "solution") =>
  `/api/plan-deal-options/${encodeURIComponent(dealId)}/${action}`;

export function useRefreshPlanDeal() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (dealId: string) => {
      const { deal } = await jsonFetch<{ deal: PlanDealOption }>(dealUrl(dealId, "refresh"), {
        method: "POST",
      });
      return withFreshSolutions(queryClient, deal);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: planDealOptionKeys.all }),
  });
}

export function useCreatePlanDealSolution() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { dealId: string; name: string }) => {
      const { deal } = await jsonFetch<{ deal: PlanDealOption }>(
        dealUrl(input.dealId, "solution"),
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ name: input.name }),
        },
      );
      return withFreshSolutions(queryClient, deal);
    },
    onSuccess: (_data, input) => {
      toast.success(`Created “${input.name.trim()}” in NOS and linked it to the Deal`);
      return queryClient.invalidateQueries({ queryKey: planDealOptionKeys.all });
    },
    onError: (err) => toast.error(err.message),
  });
}
